import type { Department } from "../../../contexts/AuthProvider";
import { buildHorizontalLayout, flattenPivotColumns } from "../layout/horizontalPivot";
import { expandColumnsForStaffPerRow } from "../layout/standardLayout";
import { isPivotActive } from "../predicates/isPivotActive";
import type { CustomTimeGroupsConfig, LaidOutSheetPlan, LayoutShape, PrintoutColumn, PrintoutColumnType, ScheduleRow, SheetPlan } from "../types";

interface PlanLayoutArgs {
	sheet: SheetPlan;
	enabledColumns: PrintoutColumn[];
	staffPerRow: number;
	rowGroupingBy: PrintoutColumnType[];
	columnGroupingBy: PrintoutColumnType | null;
	timezone: string;
	department: Department;
	jobManagementEnabled: boolean;
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	mergeSimilarShiftTimes: boolean | undefined;
	customTimeGroups: CustomTimeGroupsConfig;
	/** Heading of the band for shifts that cover no custom time group; read only when custom groups are on. */
	customTimeGroupsOtherLabel: string;
}

const flattenSheetRows = (sheet: SheetPlan): ScheduleRow[] => sheet.sections.flatMap((s) => s.rows);

/**
 * Phase 5 — pick the layout shape for one sheet (mapping §4 phase 5 / §6, §8).
 *
 * - Horizontal pivot is active iff `columnGroupingBy != null` (mapping §6.1). In that branch,
 *   `staffPerRow` is silently ignored — the pivot does the horizontal packing instead (mapping §8.2.a).
 * - Standard layout otherwise. `expandColumnsForStaffPerRow` produces the duplicated columns;
 *   the renderer enforces the packing rule (never pack across `rowGroupingBy` boundaries).
 *
 * The horizontal-pivot computation needs all rows on the sheet at once (it scans them to extract
 * the distinct axis values), so we flatten the sheet's sections into a single row list here.
 * The returned `layout.kind === "pivot"` plan shares one `HorizontalLayout` across all sections
 * on the sheet — same axis labels appear in every section's header (matching today's behavior).
 */
export const planLayout = (args: PlanLayoutArgs): LaidOutSheetPlan => {
	const {
		sheet,
		enabledColumns,
		staffPerRow,
		rowGroupingBy,
		columnGroupingBy,
		timezone,
		department,
		jobManagementEnabled,
		sortBy,
		customStringOrderMap,
		mergeSimilarShiftTimes,
		customTimeGroups,
		customTimeGroupsOtherLabel,
	} = args;

	let layout: LayoutShape;
	if (isPivotActive(columnGroupingBy)) {
		const hLayout = buildHorizontalLayout({
			rows: flattenSheetRows(sheet),
			columnGroupingBy,
			enabledColumns,
			rowGroupingBy,
			timezone,
			department,
			jobManagementEnabled,
			sortBy,
			customStringOrderMap,
			mergeSimilarShiftTimes,
			customTimeGroups,
			customTimeGroupsOtherLabel,
		});
		layout = { kind: "pivot", layout: hLayout, flatColumns: flattenPivotColumns(hLayout) };
	} else {
		const expanded = expandColumnsForStaffPerRow({ enabledColumns, staffPerRow, rowGroupingBy });
		layout = { kind: "standard", expandedColumns: expanded };
	}

	return { ...sheet, layout };
};
